// ============================================================================
//  ClaimsImport.js — Claims ▸ Import (NHIA e-Claims XML).
// ----------------------------------------------------------------------------
//  Two phases:
//    1. Pick the target facility. The XML itself carries every claim's
//       admission + discharge date, so we derive the month/year from the
//       parsed content — the operator never has to spell it out.
//    2. Drop the XML → parser returns every claim + a batch summary
//       (IPD vs OPD counts, admission date range). Review with a
//       type-of-service filter, then commit.
// ============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import notify from './notify';
import { claimsImportPreview, claimsImportCommit, claimsImportJob } from '../BaseURL';
import { IconRecords, IconCheck, IconClose, IconClaims } from './icons';
import './ClaimsImport.css';

function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

//  How much of the claim list is rendered, and how fast it grows. See the note on
//  `limit` below for why this is chunked at all.
//
//  FIRST_PAGE is what a fresh preview shows — enough to judge the file without
//  paying for the rest. CHUNK is added per animation frame after that: 75 rows is
//  roughly 1,100 DOM nodes, which commits inside a frame on the low-end laptops
//  these run on. Larger chunks finish sooner but start dropping frames, which is
//  the thing being fixed.
const FIRST_PAGE = 50;
const CHUNK = 75;

//  ── THE UPLOAD CEILING ──────────────────────────────────────────────────────
//
//  Must equal MAX_IMPORT_MB in backend/importLimits.js, where the measurements and
//  the reasoning live. A test asserts the two agree, because a browser that thinks
//  the limit is larger than the server's puts back the exact failure this fixes,
//  and one that thinks it is smaller refuses files the server would have taken.
const MAX_IMPORT_MB = 40;
const MAX_IMPORT_BYTES = MAX_IMPORT_MB * 1024 * 1024;

const humanBytes = (n) => {
  const b = Number(n);
  if (!Number.isFinite(b) || b < 0) return '0 KB';
  if (b >= 1024 * 1024) {
    const m = b / 1024 / 1024;
    return `${m < 10 ? m.toFixed(1) : Math.round(m)} MB`;
  }
  return `${Math.max(1, Math.round(b / 1024))} KB`;
};

//  The same sentence the server sends, so a file refused here and a file refused
//  there read identically. Says the file's own size and what to do about it — the
//  export window's From/To dates and specialty picker are how a month is split.
const tooLargeMessage = (bytes) => `This file is ${humanBytes(bytes)} and the limit `
  + `is ${MAX_IMPORT_MB} MB. Export it in two halves — use the From and To dates in `
  + 'the export window, or export one specialty at a time — and import each file '
  + 'separately. Nothing is lost by splitting: claims already on file are refreshed, '
  + 'not duplicated.';

//  ── WHAT WENT WRONG, IN WORDS THE OFFICER CAN ACT ON ────────────────────────
//
//  This used to be `err?.response?.data?.error || err.message`, and on the reported
//  failure both were empty of meaning: the connection was destroyed mid-upload, so
//  there was no response at all and axios's message was the bare string "Network
//  Error". The screen printed it and the officer was told nothing.
//
//  A request that dies with no response is now read for what it almost always is on
//  this screen — a file the far end would not take — and says so, naming the size.
//  The size is worth naming even when we are guessing at the cause: it is the one
//  fact that distinguishes "try again" from "split the file".
const explainUploadError = (err, f) => {
  const fromServer = err?.response?.data?.error;
  if (fromServer) return fromServer;

  const size = f?.size;
  if (!err?.response) {
    //  No response reached us. Either the upload was cut off or the server is down,
    //  and the file's size is what tells them apart for the person reading this.
    if (size && size > MAX_IMPORT_BYTES) return tooLargeMessage(size);
    return 'The upload did not finish, so the server never answered'
      + (size ? ` — the file is ${humanBytes(size)}.` : '.')
      + ' Check that you are still signed in and that the server is running, then'
      + ' try again. If it keeps happening on this file and not on smaller ones,'
      + ` split it: the limit is ${MAX_IMPORT_MB} MB.`;
  }
  if (err.response.status === 413) return tooLargeMessage(size || 0);
  return err.message || 'The import could not be read.';
};

//  A small inline spinner. Not notify.loading() — that is a SweetAlert modal, and
//  a modal over a dialog to say the dialog is busy is worse than the wait. This
//  sits where the thing being waited for will appear.
function Spinner({ size = 16 }) {
  return <span className="ci-spinner" style={{ width: size, height: size }} aria-hidden="true" />;
}

//  `onImported(job)` fires ONCE, when a commit finishes with status 'done'. The
//  panel around this uses it to close the dialog and pull the new batch into the
//  records list, which is why it carries the finished job rather than being a bare
//  ping — the caller can name what landed.
//
//  It was polled for on a timer before, because this component exposed no
//  completion hook: the records card refreshed itself every 8 seconds whether or
//  not anything had happened, and a finished import appeared up to 8 seconds late.
export default function ClaimsImport({ facilities = [], onImported }) {
  // ── Phase 1: pick the facility ──────────────────────────────
  const [facilityId, setFacilityId] = useState('');
  const ready = !!facilityId;

  // ── Phase 2: file + preview ─────────────────────────────────
  const [file, setFile]         = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview]   = useState(null);
  const [committing, setCommitting] = useState(false);
  const [job, setJob] = useState(null);              // { id, status, processed, inserted, ... }
  const [dragOver, setDragOver] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const [attFilter, setAttFilter] = useState(''); // '' | 'inpatient' | 'outpatient'
  const fileInput = useRef(null);

  //  ── Why the list is rendered in chunks ──────────────────────────────────
  //  "Show all 494" used to flip one boolean and hand React 494 claim rows in a
  //  single commit. Each row is a button, three text lines, four counters and a
  //  flag — call it fifteen nodes — so that is 7,000-odd nodes built, diffed and
  //  laid out without ever yielding. The tab freezes for seconds and the screen
  //  shows NOTHING, because the browser cannot paint while the main thread is
  //  inside React's commit.
  //
  //  A spinner does not fix that. There is no frame in which to draw one: the
  //  freeze IS the absence of frames. The only fix is to stop doing the work in
  //  one block — grow the rendered count a chunk at a time and let the browser
  //  paint between chunks. That gets a responsive page AND a progress line that
  //  actually appears, because now there are frames in which to draw it.
  const [limit, setLimit] = useState(FIRST_PAGE);
  const [growing, setGrowing] = useState(false);

  const facility = useMemo(() =>
    facilities.find((f) => String(f.id) === String(facilityId)),
  [facilities, facilityId]);

  //  useCallback so `runPreview` can depend on it without being rebuilt on every
  //  render. Every call inside is a setter or a ref, so the identity is genuinely
  //  stable rather than merely declared to be.
  const resetFile = useCallback(() => {
    setFile(null); setPreview(null); setJob(null); setExpanded(new Set());
    setLimit(FIRST_PAGE); setGrowing(false); setAttFilter('');
    if (fileInput.current) fileInput.current.value = '';
  }, []);
  const resetAll = () => { resetFile(); setFacilityId(''); };

  const runPreview = useCallback(async (f) => {
    //  ── TOO BIG IS ANSWERED HERE, BEFORE ANYTHING IS SENT ────────────────────
    //
    //  The server refuses an oversized upload too, but it can only do so once the
    //  file is on its way — and a refusal written to a half-finished upload gets
    //  the socket destroyed under it, which the browser reports as "Network Error"
    //  with no response to read. That is the whole of the reported bug: a 29 MB
    //  May export, refused by a 20 MB cap, arriving on screen as a network fault.
    //
    //  The size is known before a single byte leaves, so it is checked before a
    //  single byte leaves. It also spares a hospital connection the 29 MB upload
    //  that was always going to be thrown away.
    if (f && f.size > MAX_IMPORT_BYTES) {
      notify.error('That file is too large to import', tooLargeMessage(f.size));
      resetFile();
      return;
    }
    setFile(f);
    setPreview(null);
    setJob(null);
    setPreviewing(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const { data } = await axios.post(claimsImportPreview, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(data);
    } catch (err) {
      notify.error('Preview failed', explainUploadError(err, f));
      setFile(null);
    } finally { setPreviewing(false); }
  }, [resetFile]);

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    if (!ready) return;
    const f = e.dataTransfer.files?.[0];
    if (f) runPreview(f);
  };
  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (f) runPreview(f);
  };

  const commit = async () => {
    if (!file) return notify.warning('Drop or pick an XML file first');
    if (!ready) return notify.warning('Pick a facility first');
    setCommitting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('facilityId', facilityId);
      const { data } = await axios.post(claimsImportCommit, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Server returns { jobId, total, batch } — start polling.
      setJob({ id: data.jobId, status: 'running', total: data.total, processed: 0, inserted: 0, skipped: 0 });
    } catch (err) {
      notify.error('Import failed', explainUploadError(err, file));
      setCommitting(false);
    }
  };

  // Poll the job endpoint every 500ms until status changes off 'running'.
  useEffect(() => {
    if (!job || job.status !== 'running') return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await axios.get(claimsImportJob(job.id));
        if (cancelled) return;
        setJob(data.job);
        if (data.job.status === 'running') setTimeout(tick, 500);
        else {
          setCommitting(false);
          if (data.job.status === 'done') {
            notify.success('Import complete',
              `${data.job.inserted} claim(s) written${data.job.skipped ? `, ${data.job.skipped} skipped` : ''}.`);
            //  Told to the caller rather than left to a polling timer. The finished
            //  job goes with it so the records list can name the batch that landed.
            if (typeof onImported === 'function') onImported(data.job);
          } else if (data.job.status === 'failed') {
            notify.error('Import failed', data.job.error || 'Unknown error');
          }
        }
      } catch (err) {
        if (!cancelled) setTimeout(tick, 1500);
      }
    };
    setTimeout(tick, 400);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, job?.status]);

  const toggleExpand = (i) => setExpanded((s) => {
    const n = new Set(s); n.has(i) ? n.delete(i) : n.add(i); return n;
  });

  // ── The filters ───────────────────────────────────────────────────────────
  //  Applied client-side against the whole parsed set, so the pills feel instant
  //  even at 4,000+ rows.
  //
  //  `warnings` and `duplicates` are what this screen was missing. It said
  //  "69 of 494 claim(s) have validation warnings — review before committing" and
  //  gave no way to review them: the warnings are on 69 rows scattered through
  //  494, and finding them meant scrolling. Now they are one click.
  const filteredClaims = useMemo(() => {
    if (!preview) return [];
    const all = preview.claims || [];
    if (attFilter === 'warnings')   return all.filter((c) => !c.isDuplicate && c.problems?.length);
    if (attFilter === 'duplicates') return all.filter((c) => c.isDuplicate);
    if (attFilter === 'inpatient' || attFilter === 'outpatient') {
      return all.filter((c) => c.attendanceType === attFilter);
    }
    //  Anything the NHIA file typed as neither — pharmacy, dental, eye. The pill
    //  for these was rendered without an onClick, so it lit up as active whenever
    //  any non-IPD/OPD filter was chosen and could never be selected itself.
    if (attFilter === 'other') {
      return all.filter((c) => !['inpatient', 'outpatient'].includes(c.attendanceType));
    }
    return all;
  }, [preview, attFilter]);

  //  Counted over the whole set, never over the filtered view — a pill whose
  //  count changed when you clicked another pill would be describing the filter
  //  rather than the file.
  const warnCount = useMemo(
    () => (preview?.claims || []).filter((c) => !c.isDuplicate && c.problems?.length).length,
    [preview]);
  const dupCount = useMemo(
    () => (preview?.claims || []).filter((c) => c.isDuplicate).length,
    [preview]);

  //  Which warnings, and how many claims carry each. The banner said "69 claims
  //  have warnings" and stopped; whether that is 69 missing names or 69 missing
  //  diagnoses is the difference between a data-entry problem at the facility and
  //  a mapping problem in the file.
  const warnBreakdown = useMemo(() => {
    const counts = {};
    for (const c of preview?.claims || []) {
      if (c.isDuplicate) continue;
      for (const p of c.problems || []) counts[p] = (counts[p] || 0) + 1;
    }
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [preview]);

  //  ── The chunked growth loop ─────────────────────────────────────────────
  //  TWO nested frames, not one. React commits the rows added by the last
  //  setLimit during the first callback's frame; asking for the next chunk in
  //  that same callback would queue it before the browser has painted, and the
  //  chunks would coalesce back into one long block — the freeze again, just
  //  spelled differently. The second frame is the one that guarantees a paint
  //  landed in between.
  useEffect(() => {
    if (!growing) return undefined;
    if (limit >= filteredClaims.length) { setGrowing(false); return undefined; }
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => {
        setLimit((n) => Math.min(n + CHUNK, filteredClaims.length));
      });
    });
    //  Cancelled if the filter changes or the dialog closes mid-grow, so a
    //  pending frame cannot land a setState on an unmounted tree.
    return () => { cancelAnimationFrame(outer); if (inner) cancelAnimationFrame(inner); };
  }, [growing, limit, filteredClaims.length]);

  //  ── Changing filter, in one place ───────────────────────────────────────
  //  Every filter change starts over at one page. Carrying a 494-row limit into a
  //  filter matching 69 claims is harmless, but carrying it the other way — from
  //  a small filter back to All — renders everything in one commit and brings the
  //  freeze straight back.
  //
  //  A FUNCTION, not an effect keyed on attFilter. As an effect it fought the
  //  "Review the 69" button: that sets the filter and asks to grow in the same
  //  handler, both land in one batch, and then the effect fires because attFilter
  //  changed and resets growing to false. The grow never happened. Here the
  //  caller states which it wants and there is no ordering to lose.
  const changeFilter = useCallback((next, opts = {}) => {
    setAttFilter(next);
    setLimit(FIRST_PAGE);
    setGrowing(!!opts.grow);
  }, []);

  const visibleClaims = useMemo(
    () => filteredClaims.slice(0, limit),
    [filteredClaims, limit]);

  const allShown = limit >= filteredClaims.length;

  //  The batch-level warnings worth showing — i.e. not the one the panel above
  //  already says better. Matched on the phrase rather than an index because the
  //  server builds this list conditionally and the position is not stable.
  const batchWarnings = useMemo(
    () => (preview?.warnings || []).filter((w) => !/validation warnings/i.test(w)),
    [preview]);

  const attendanceBreakdown = preview?.batch?.attendanceBreakdown || {};
  const ipdCount = attendanceBreakdown.inpatient  || 0;
  const opdCount = attendanceBreakdown.outpatient || 0;
  const otherCount = Object.entries(attendanceBreakdown)
    .filter(([k]) => k !== 'inpatient' && k !== 'outpatient')
    .reduce((n, [, v]) => n + v, 0);

  return (
    <div className="ci">
      {/* ── Phase 1: facility gate ─────────────────────────────── */}
      <section className={`ci-gate ${ready ? 'is-ready' : ''}`}>
        <div className="ci-gate-step">
          <span className="ci-step-num">1</span>
          <div>
            <div className="ci-step-title">Which facility is this batch for?</div>
            <div className="ci-step-sub">
              Pick the hospital that produced this XML. The month and year come
              straight from each claim's <code>dateOfService</code>, so we don't
              ask you to pick them.
            </div>
          </div>
        </div>
        <select className="ci-gate-select" value={facilityId} onChange={(e) => setFacilityId(e.target.value)} disabled={committing}>
          <option value="">— pick a facility —</option>
          {facilities.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}{f.short_code ? ` (${f.short_code})` : ''}
            </option>
          ))}
        </select>

        {ready && (
          <div className="ci-gate-summary">
            <IconCheck size={16} /> <strong>{facility?.name}</strong>
            {preview && (
              <span style={{ color: 'var(--text-muted)', marginLeft: 6 }}>
                · {preview.batch.monthYear || '—'} · {fmtDate(preview.batch.admissionRange?.min)} → {fmtDate(preview.batch.admissionRange?.max)}
              </span>
            )}
          </div>
        )}
      </section>

      {/* ── Phase 2: drop zone ─────────────────────────────────── */}
      <section
        className={`ci-drop ${dragOver ? 'is-hover' : ''} ${file ? 'has-file' : ''} ${!ready ? 'is-disabled' : ''}`}
        onDragOver={(e) => { e.preventDefault(); if (ready) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        // The whole zone opens the file dialog, not just the small link that
        // used to be the only way in besides dragging. Guarded so it does not
        // fire when a file is already chosen (the row then has its own
        // "Choose a different file" control) or before a facility is picked.
        onClick={() => { if (ready && !file) fileInput.current?.click(); }}
        role={ready && !file ? 'button' : undefined}
        tabIndex={ready && !file ? 0 : undefined}
        onKeyDown={(e) => {
          if (ready && !file && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            fileInput.current?.click();
          }
        }}
      >
        <input
          type="file"
          accept=".xml,application/xml,text/xml"
          hidden
          ref={fileInput}
          onChange={onPickFile}
          disabled={!ready}
        />
        {!file ? (
          <>
            <div className="ci-step-num" style={{ marginBottom: 12 }}>2</div>
            <div className="ci-drop-icon"><IconRecords size={32} /></div>
            <div className="ci-drop-title">Drop the NHIA e-Claims XML</div>
            <div className="ci-drop-sub">
              {ready
                ? 'Drag the file here, or browse for it.'
                : 'Pick a facility above to unlock the drop zone.'}
            </div>
            {/* A real button rather than the inline text link this used to be.
                stopPropagation because the section itself now opens the dialog —
                without it the click would bubble and open two file pickers. */}
            {ready && (
              <button
                type="button"
                className="ci-browse-btn"
                onClick={(e) => { e.stopPropagation(); fileInput.current?.click(); }}
              >
                Browse for XML file
              </button>
            )}
          </>
        ) : (
          <div className="ci-drop-file">
            <div>
              <div className="ci-drop-title">{file.name}</div>
              <div className="ci-drop-sub">
                {(file.size / 1024).toFixed(1)} KB
                {preview && <> · <strong>{preview.batch.count}</strong> claim(s) parsed · <strong>{preview.batch.monthYear || '—'}</strong></>}
              </div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={resetFile}>Choose a different file</button>
          </div>
        )}
      </section>

      {/*  ── Parsing ───────────────────────────────────────────────────────────
           This wait is a real one — the file uploads, the server walks the XML and
           the response for a 494-claim batch is a few megabytes of JSON. It used to
           be one line of grey text, which is easy to miss under a drop zone that
           still looks idle. Now it says what stage it is at and roughly how big the
           job is, so a long wait reads as work rather than as a hang. */}
      {previewing && (
        <div className="ci-busy" role="status" aria-live="polite">
          <Spinner size={20} />
          <div>
            <div className="ci-busy-title">Reading the XML…</div>
            <div className="ci-busy-sub">
              {file
                ? <>{file.name} · {(file.size / 1024 / 1024).toFixed(2)} MB. Large batches
                    take a few seconds — the file is uploaded, every claim is parsed and
                    each code is matched against your catalogue before anything is shown.</>
                : <>The file is uploaded and every claim parsed before anything is shown.</>}
            </div>
          </div>
        </div>
      )}

      {/* ── Warnings ──────────────────────────────────────────────────────────
          "69 of 494 claim(s) have validation warnings — review before
          committing" reads as a gate: fix these or they will be dropped. They are
          NOT dropped. Nothing is, except a claim already in the database — the
          importer recovers a missing date from the discharge date or the batch
          month, and a missing name or diagnosis does not stop an insert.

          So the banner now says what actually happens, and breaks the warnings
          down by KIND. 69 missing names is a data-entry problem at the facility;
          69 missing diagnoses is a mapping problem in the file. The old wording
          could not tell them apart. */}
      {preview && !job && warnCount > 0 && (
        <div className="ci-warn-panel">
          <div className="ci-warn-head">
            <span className="ci-warn-badge">{warnCount.toLocaleString()}</span>
            <div>
              <div className="ci-warn-title">
                {warnCount.toLocaleString()} of {preview.claims.length.toLocaleString()} claim(s)
                carry a warning
              </div>
              {/*  Kept in step with ictClaimsImport.js, which now drops almost
                   nothing: a missing date falls back to a flagged placeholder, a
                   claim already on file is refreshed or kept, and a claim whose
                   detail lines fail is retried on its own and lands without them.
                   The ONE remaining skip is a claim that cannot be written even
                   alone. Overstating the guarantee and understating it are the same
                   failure, so this says the actual number: one. */}
              <div className="ci-warn-lead">
                <strong>They will all be imported.</strong> A warning never drops a
                claim — a missing name, a missing diagnosis or an unknown code is
                written as it stands and flagged on the claims list afterwards, so it
                can be fixed there. A claim is only ever skipped if the database
                refuses it outright, and every one of those is named with its reason
                in the report at the end.
              </div>
            </div>
            <button
              type="button"
              className="ci-warn-review"
              onClick={() => changeFilter('warnings', { grow: true })}
            >
              Review the {warnCount.toLocaleString()}
            </button>
          </div>

          {/*  By kind, with counts. This is the part that says what is actually
               wrong with the file. */}
          <ul className="ci-warn-kinds">
            {warnBreakdown.map(([kind, n]) => (
              <li key={kind}>
                <span className="ci-warn-kind-n">{n.toLocaleString()}</span>
                {kind}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/*  Whatever else the parser reported about the batch as a whole.

           The server also sends a batch-level "N of M claim(s) have validation
           warnings — review before committing", which is the exact sentence the
           panel above replaced: it duplicates the count and re-asserts the thing
           that is not true, that a warning is a gate. Dropped HERE rather than in
           the backend, because that string is part of the import job's stored
           report and other readers of it are out of scope for a UI change. */}
      {batchWarnings.length > 0 && (
        <div className="ci-warnings">
          {batchWarnings.map((w, i) => <div key={i} className="ci-warning">⚠ {w}</div>)}
        </div>
      )}

      {/*  ── Uploading, before the job exists ─────────────────────────────────
           The gap nobody was covering. Commit re-uploads the whole file and the
           server creates the job before responding, so for several seconds on a
           multi-megabyte batch the only feedback was the button changing to
           "Committing…" — at the far right of a bar that may be scrolled out of
           view. The progress bar cannot help yet because there is no job id to
           poll. This fills exactly that window and disappears the moment the
           first job response lands. */}
      {committing && !job && (
        <div className="ci-busy is-commit" role="status" aria-live="polite">
          <Spinner size={20} />
          <div>
            <div className="ci-busy-title">Sending the batch to the server…</div>
            <div className="ci-busy-sub">
              Uploading {file ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : 'the file'} and
              starting the import job. Progress appears here as soon as the first claim is
              written. Do not close this dialog yet.
            </div>
          </div>
        </div>
      )}

      {/* ── Progress bar while a job is running ─────────────────── */}
      {job && (
        <JobPanel
          job={job}
          facilityName={facility?.name}
          batch={preview?.batch}
          onReset={resetFile}
          onResetAll={resetAll}
        />
      )}

      {/* ── Commit bar + IPD/OPD breakdown ────────────────────── */}
      {preview && !job && (
        <section className="ci-commit-bar">
          <div className="ci-commit-stats">
            {/*  "To insert" and "Duplicates" implied the second number was
                 subtracted from the first. It is not, any more: a claim already on
                 file is refreshed from this file, or kept if it has been edited
                 here since. Both are processed, so the total is the total. */}
            <div>
              <span>Claims in file</span>
              <strong>{preview.claims.length}</strong>
            </div>
            <div>
              <span>Already on file</span>
              <strong>{dupCount}</strong>
            </div>
            <div>
              <span>Inpatient (IPD)</span>
              <strong style={{ color: '#7c3aed' }}>{ipdCount.toLocaleString()}</strong>
            </div>
            <div>
              <span>Outpatient (OPD)</span>
              <strong style={{ color: '#0ea5e9' }}>{opdCount.toLocaleString()}</strong>
            </div>
          </div>
          <div className="ci-commit-act">
            {/*  Says what the button will and will not do, next to the button. The
                 count on it already excludes duplicates; without this line that
                 exclusion is a silent subtraction the operator has to work out
                 from two numbers in the strip beside it. */}
            <div className="ci-commit-note">
              All {preview.claims.length.toLocaleString()} are processed, warnings
              included.
              {dupCount > 0 && <> The {dupCount.toLocaleString()} already on file are
                refreshed from this file unless edited here since.</>}
              {warnCount > 0 && <> {warnCount.toLocaleString()} carry a warning.</>}
            </div>
            {/*  The count is the WHOLE file. It used to subtract duplicates, which
                 was right when a duplicate was dropped and is now misleading — a
                 duplicate is refreshed or kept, and either way it is worked on. */}
            <button className="btn btn-primary" onClick={commit} disabled={committing}>
              {committing
                ? 'Committing…'
                : `Commit ${preview.claims.length.toLocaleString()} claim(s)`}
            </button>
          </div>
        </section>
      )}

      {/* ── Preview grid ──────────────────────────────────────── */}
      {preview && !job && (
        <section className="ci-preview">
          <div className="ci-preview-toolbar">
            <div className="ci-preview-pills">
              <button className={`ci-pill ${attFilter === '' ? 'is-active' : ''}`} onClick={() => changeFilter('')}>
                All <span>{preview.claims.length.toLocaleString()}</span>
              </button>
              <button className={`ci-pill ci-pill-ipd ${attFilter === 'inpatient' ? 'is-active' : ''}`}
                      onClick={() => changeFilter('inpatient')} disabled={ipdCount === 0}>
                Inpatient · IPD <span>{ipdCount.toLocaleString()}</span>
              </button>
              <button className={`ci-pill ci-pill-opd ${attFilter === 'outpatient' ? 'is-active' : ''}`}
                      onClick={() => changeFilter('outpatient')} disabled={opdCount === 0}>
                Outpatient · OPD <span>{opdCount.toLocaleString()}</span>
              </button>
              {otherCount > 0 && (
                <button className={`ci-pill ${attFilter === 'other' ? 'is-active' : ''}`}
                        onClick={() => changeFilter('other')}>
                  Other <span>{otherCount}</span>
                </button>
              )}

              {/*  ── The two that matter before committing ──────────────────
                   A warning does not stop a claim importing, but it is the list
                   you want to read first, and it was unreachable: 69 flagged rows
                   scattered through 494 with only a scrollbar to find them.

                   Duplicates get their own pill for the opposite reason — they
                   are the ONLY thing that will not be imported, so being able to
                   see exactly which claims those are is the difference between
                   trusting the count and taking it on faith. */}
              {warnCount > 0 && (
                <button className={`ci-pill ci-pill-warn ${attFilter === 'warnings' ? 'is-active' : ''}`}
                        onClick={() => changeFilter('warnings')}
                        title="Claims with a validation warning — all of these still import">
                  ⚠ Warnings <span>{warnCount.toLocaleString()}</span>
                </button>
              )}
              {dupCount > 0 && (
                <button className={`ci-pill ci-pill-dup ${attFilter === 'duplicates' ? 'is-active' : ''}`}
                        onClick={() => changeFilter('duplicates')}
                        title={'Already on file. Each is refreshed from this file, unless it has been '
                             + 'edited here since — then your edits are kept and the file’s version '
                             + 'is ignored. The report says which happened to each one.'}>
                  Already on file <span>{dupCount.toLocaleString()}</span>
                </button>
              )}
            </div>

            {/*  ── The loading indicator for the list ────────────────────────
                 While the chunks land this replaces the show-all control with a
                 live count. It is the answer to "nothing is showing on the
                 interface": there is now something to show, and — because the
                 growth yields a frame per chunk — frames in which to show it. */}
            {growing ? (
              <div className="ci-growing" role="status" aria-live="polite">
                <Spinner size={14} />
                <span>
                  Rendering <strong>{limit.toLocaleString()}</strong> of{' '}
                  {filteredClaims.length.toLocaleString()}…
                </span>
                <button className="link-btn" onClick={() => setGrowing(false)}>Stop</button>
              </div>
            ) : filteredClaims.length > FIRST_PAGE && (
              <button
                className="link-btn"
                onClick={() => {
                  if (allShown) { setLimit(FIRST_PAGE); setGrowing(false); }
                  else setGrowing(true);
                }}
              >
                {allShown
                  ? `Show first ${FIRST_PAGE} only`
                  : `Show all ${filteredClaims.length.toLocaleString()}`}
              </button>
            )}
          </div>

          <ol className="ci-claims">
            {visibleClaims.map((c, i) => {
              const isOpen = expanded.has(i);
              const patient = [c.surname, c.otherNames].filter(Boolean).join(', ') || <em style={{ color: 'var(--text-muted)' }}>(unnamed)</em>;
              const attCls = c.attendanceType === 'inpatient' ? 'is-ipd' :
                             c.attendanceType === 'outpatient' ? 'is-opd' : '';
              const attLabel = c.attendanceType === 'inpatient'  ? 'IPD' :
                               c.attendanceType === 'outpatient' ? 'OPD' :
                               c.attendanceType;
              return (
                <li key={i} className={`ci-claim ${c.isDuplicate ? 'is-dup' : ''} ${c.problems?.length ? 'has-warn' : ''}`}>
                  <button type="button" className="ci-claim-head" onClick={() => toggleExpand(i)}>
                    <span className="ci-claim-idx">{i + 1}</span>
                    <span className={`ci-claim-att ${attCls}`}>{attLabel}</span>
                    <div className="ci-claim-main">
                      <div className="ci-claim-line1">
                        <code className="ci-claim-ref">{c.sourceRef}</code>
                        <span className="ci-claim-name">{patient}</span>
                        <span className="ci-claim-date">
                          {c.admissionDate}
                          {c.dischargeDate && c.dischargeDate !== c.admissionDate && <> → {c.dischargeDate}</>}
                        </span>
                      </div>
                      <div className="ci-claim-line2">
                        {c.memberNo && <>Member <code>{c.memberNo}</code></>}
                        {c.hospitalRecNo && <> · Rec <code>{c.hospitalRecNo}</code></>}
                        {c.outcome && c.outcome !== 'pending' && <> · {c.outcome}</>}
                      </div>
                    </div>
                    <div className="ci-claim-counts">
                      <Count label="Dx"    n={c.counts.diagnoses} />
                      <Count label="Proc"  n={c.counts.procedures} />
                      <Count label="Inv"   n={c.counts.investigations} />
                      <Count label="Meds"  n={c.counts.medications} />
                    </div>
                    <div className="ci-claim-flags">
                      {c.isDuplicate && <span className="ci-flag is-dup">Duplicate</span>}
                      {!c.isDuplicate && c.problems?.length > 0 && <span className="ci-flag is-warn">{c.problems.length} warning</span>}
                      {!c.isDuplicate && c.problems?.length === 0 && <span className="ci-flag is-ok"><IconCheck size={12} /> Ready</span>}
                    </div>
                  </button>

                  {isOpen && (
                    <div className="ci-claim-body">
                      {c.problems?.length > 0 && (
                        <ul className="ci-claim-problems">
                          {c.problems.map((p) => <li key={p}><IconClose size={12} /> {p}</li>)}
                        </ul>
                      )}
                      <ChildBlock label="Diagnoses" items={c.children.diagnoses}
                        row={(d) => `${d.icd10Code || '—'} · ${d.description}${d.gdrgCode ? `  ⟨${d.gdrgCode}⟩` : ''}`} />
                      <ChildBlock label="Procedures" items={c.children.procedures}
                        row={(p) => `${p.gdrgCode || '—'} · ${p.description}`} />
                      <ChildBlock label="Investigations" items={c.children.investigations}
                        row={(iv) => `${iv.code || '—'} · ${iv.description || iv.code}`} />
                      <ChildBlock label="Medications" items={c.children.medications}
                        row={(m) =>
                          `${m.code || '—'} · qty ${m.quantity}${m.dose ? ` · ${m.dose}` : ''}${m.frequency ? ` · ${m.frequency}` : ''}${m.duration ? ` · ${m.duration}` : ''}`
                        } />
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
          {!allShown && !growing && (
            <div className="ci-more">
              Showing {limit.toLocaleString()} of {filteredClaims.length.toLocaleString()} claims.
              All will be imported on commit — the preview is a sample so the page stays
              responsive.
              <button className="link-btn" onClick={() => setGrowing(true)}>
                Load the rest
              </button>
            </div>
          )}
        </section>
      )}

      {!file && !previewing && ready && (
        <div className="ci-hint">
          <IconClaims size={14} />
          <span>
            The parser reads NHIA e-Claims XML directly. The month/year and IPD/OPD split come from each claim's own <code>dateOfService</code> and <code>typeOfService</code> — no separate picker needed.
          </span>
        </div>
      )}
    </div>
  );
}

function Count({ label, n }) {
  return (
    <div className={`ci-count ${n === 0 ? 'is-zero' : ''}`}>
      <span>{label}</span>
      <strong>{n}</strong>
    </div>
  );
}

function ChildBlock({ label, items = [], row }) {
  if (items.length === 0) return null;
  return (
    <div className="ci-child-block">
      <div className="ci-child-label">{label} · {items.length}</div>
      <ul className="ci-child-list">
        {items.map((it, i) => (
          <li key={i}>
            <span>{row(it)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Job panel — progress bar while running, report when done ─────────────
function JobPanel({ job, facilityName, batch, onReset, onResetAll }) {
  const running = job.status === 'running';
  const done    = job.status === 'done';
  const failed  = job.status === 'failed';
  const total   = Number(job.total) || 0;
  const pct     = total > 0 ? Math.min(100, Math.round((job.processed / total) * 100)) : 0;

  // Parse the JSON report if the backend hasn't already.
  const report = useMemo(() => {
    if (!job.report_json) return null;
    if (typeof job.report_json === 'string') {
      try { return JSON.parse(job.report_json); } catch { return null; }
    }
    return job.report_json;
  }, [job.report_json]);

  return (
    <section className={`ci-job ${running ? 'is-running' : done ? 'is-done' : 'is-failed'}`}>
      <header className="ci-job-head">
        <div>
          <h3>
            {running ? 'Importing…' : done ? 'Import complete' : 'Import failed'}
          </h3>
          <div className="ci-job-meta">
            <strong>{facilityName}</strong>
            {batch?.monthYear && <> · {batch.monthYear}</>}
            {batch?.admissionRange?.min && (
              <span style={{ color: 'var(--text-muted)' }}>
                {' '}· {fmtDate(batch.admissionRange.min)} → {fmtDate(batch.admissionRange.max)}
              </span>
            )}
          </div>
        </div>
        {running && (
          <div className="ci-job-percent">{pct}%</div>
        )}
      </header>

      {/* Progress bar (always shown; 100% when done) */}
      <div className="ci-progress">
        <div className="ci-progress-track">
          <div className="ci-progress-fill" style={{ width: `${done ? 100 : pct}%` }} />
        </div>
        <div className="ci-progress-labels">
          <span>{job.processed.toLocaleString()} of {total.toLocaleString()} processed</span>
          <span className="ci-progress-mid">
            <span style={{ color: '#22c55e' }}>{job.inserted.toLocaleString()} inserted</span>
            {' · '}
            <span style={{ color: '#f59e0b' }}>{job.skipped.toLocaleString()} skipped</span>
          </span>
        </div>
      </div>

      {failed && (
        <div className="ci-job-error">
          <strong>Error:</strong> {job.error}
        </div>
      )}

      {/* Report — resolved / unmatched counts + drill-down */}
      {done && report && (
        <div className="ci-report">
          <h4>Resolution report</h4>
          <div className="ci-report-grid">
            {['diagnoses','procedures','investigations','medications'].map((kind) => {
              const resolved   = report.resolved?.[kind] || 0;
              const unmatched  = Object.keys(report.unmatched?.[kind] || {}).length;
              const rows       = Object.entries(report.unmatched?.[kind] || {})
                                       .sort(([, a], [, b]) => b.count - a.count)
                                       .slice(0, 12);
              const KIND_META = {
                diagnoses:      { label: 'Diagnoses',      hue: 210 },
                procedures:     { label: 'Procedures',     hue: 275 },
                investigations: { label: 'Investigations', hue: 195 },
                medications:    { label: 'Medications',    hue:  35 },
              };
              const meta = KIND_META[kind];
              return (
                <div key={kind} className="ci-report-block">
                  <div className="ci-report-block-head">
                    <span className="ci-report-kind" style={{ background: `hsl(${meta.hue} 60% 55% / 0.15)`, color: `hsl(${meta.hue} 55% 40%)` }}>{meta.label}</span>
                    <div className="ci-report-nums">
                      <span title="Codes matched to your NHIS catalogue">
                        <IconCheck size={12} /> {resolved.toLocaleString()} resolved
                      </span>
                      {unmatched > 0 && (
                        <span className="ci-report-unmatched" title="Codes with no match in the catalogue">
                          {unmatched} unknown code(s)
                        </span>
                      )}
                    </div>
                  </div>
                  {rows.length > 0 && (
                    <details className="ci-report-details">
                      <summary>Show unknown codes ({unmatched})</summary>
                      <ul className="ci-report-code-list">
                        {rows.map(([code, info]) => (
                          <li key={code}>
                            <code>{code}</code>
                            <span className="ci-report-code-count">×{info.count.toLocaleString()}</span>
                            <span className="ci-report-code-samples">
                              e.g. {info.sample.slice(0, 3).map((s) => <code key={s}>{s}</code>)}
                            </span>
                          </li>
                        ))}
                        {Object.keys(report.unmatched[kind]).length > rows.length && (
                          <li style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                            + {Object.keys(report.unmatched[kind]).length - rows.length} more
                          </li>
                        )}
                      </ul>
                    </details>
                  )}
                </div>
              );
            })}
          </div>

          {/*  EVERY SKIPPED CLAIM, BY NAME AND REASON. "5 skipped" with no reason
               is the thing that made this untrustworthy — you could not tell a
               duplicate that SHOULD be skipped from a claim lost to a bad row. */}
          {report.skipped?.length > 0 && (
            <div className="ci-report-issues">
              {Object.entries(
                report.skipped.reduce((acc, s) => {
                  (acc[s.reason] = acc[s.reason] || []).push(s);
                  return acc;
                }, {})
              ).map(([reason, list]) => (
                <div className="ci-report-issue" key={reason}>
                  <div>
                    <strong>{list.length}</strong> claim(s) skipped — {reason}.
                  </div>
                  <div style={{ fontSize: 12, marginTop: 4, opacity: 0.85 }}>
                    {list[0].detail}
                  </div>
                  <div style={{ fontSize: 11.5, marginTop: 4, opacity: 0.75,
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                    {list.slice(0, 12).map((s) => s.ref).join(', ')}
                    {list.length > 12 && ` … and ${list.length - 12} more`}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/*  Claims that WOULD have been skipped and were saved instead. A
               recovered value has to be visible, or it is just a silent guess. */}
          {report.recovered?.length > 0 && (
            <div className="ci-report-issues">
              <div className="ci-report-issue">
                <div>
                  <strong>{report.recovered.length}</strong> claim(s) were saved with a
                  recovered date rather than being skipped.
                </div>
                <div style={{ fontSize: 12, marginTop: 4, opacity: 0.85 }}>
                  Their file carried no date of service, so the discharge date — or
                  failing that, the batch month — was used. Check these before
                  submitting.
                </div>
                <div style={{ fontSize: 11.5, marginTop: 4, opacity: 0.75,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                  {report.recovered.slice(0, 12)
                    .map((r) => `${r.ref} (${r.from})`).join(', ')}
                  {report.recovered.length > 12
                    && ` … and ${report.recovered.length - 12} more`}
                </div>
              </div>
            </div>
          )}

          <div className="ci-report-actions">
            <button className="btn btn-ghost" onClick={onReset}>Import another file (same facility)</button>
            <button className="btn btn-ghost" onClick={onResetAll}>Start over</button>
          </div>
        </div>
      )}
    </section>
  );
}
